import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Coffee, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Missing reset token. Please open the link from your email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-taupe-100">
      <form
        onSubmit={submit}
        className="w-full max-w-md card-soft p-8 sm:p-10 animate-fade-in-up"
        data-testid="reset-password-form"
      >
        <div className="flex items-center gap-2 mb-6 text-espresso-800">
          <Coffee className="w-6 h-6" />
          <span className="font-heading font-bold text-lg">Brew Books</span>
        </div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">
          Set a new password
        </div>
        <h2 className="font-heading font-extrabold text-3xl tracking-tighter text-espresso-900">
          Reset password
        </h2>
        <p className="text-taupe-600 mt-2 text-sm">Choose a new password for your account.</p>

        {done ? (
          <div
            className="mt-6 flex items-center gap-3 text-sm text-matcha-500 bg-matcha-100 border border-matcha-500/20 rounded-xl px-4 py-4"
            data-testid="reset-success"
          >
            <CheckCircle2 className="w-5 h-5" />
            <div>Password updated. Redirecting to sign in…</div>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-espresso-800 mb-2 block">
                New password
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-taupe-100 border-taupe-200 rounded-xl h-12"
                data-testid="reset-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-sm font-semibold text-espresso-800 mb-2 block">
                Confirm password
              </Label>
              <Input
                id="confirm"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="bg-taupe-100 border-taupe-200 rounded-xl h-12"
                data-testid="reset-confirm-input"
              />
            </div>

            {error && (
              <div
                className="text-sm text-terracotta-500 bg-terracotta-100 border border-terracotta-500/20 rounded-xl px-4 py-3"
                data-testid="reset-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold text-base"
              data-testid="reset-submit-button"
            >
              {submitting ? "Saving…" : "Update password"}
            </Button>

            <Link
              to="/login"
              className="block text-center text-sm text-taupe-600 hover:text-espresso-800 transition-colors"
              data-testid="reset-back-to-login"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}
